import { getStrategyForRoleType } from './topicStrategies';
import { getCustomComponent } from './topicDecisionComponents';
import { StrategyPhase } from './topicStrategies/baseStrategy';
import axios from 'axios';
import centralDiscoveryQueue from './centralDiscoveryQueue';

/**
 * Simplified Topic Decision Engine
 * 
 * This utility handles the decision-making process for topic discovery with a simplified approach:
 * 1. Check if the current topic is complete
 * 2. If complete, move to the next topic and directly call initPhase
 * 3. If not complete, continue with the current topic
 */

/**
 * Process user message and determine the appropriate action
 * @param {string} userMessage - The user's message
 * @param {Array} messages - Current conversation messages
 * @param {Object} currentDiscoveryState - Current discovery state
 * @param {Function} advanceToNextTopic - Function to advance to the next topic
 * @param {Object} queries - Search queries
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Decision object with action type and data
 */
/**
 * Check if the message is a skip request from the SkipTopicButton or a move-on request
 * @param {string} message - The message to check
 * @returns {boolean} True if the message is a skip request or move-on request
 */
const isSkipRequest = (message) => {
  // Check for the standard skip request format from SkipTopicButton (old format)
  const isOldFormatSkip = message && 
    message.includes("I'd like to skip the") && 
    message.includes("discovery and move on to the next topic");
  
  // Check for the new standard skip request format
  const isNewFormatSkip = message && 
    message.includes("I am done with my selections for") && 
    message.includes("and am ready to move on to the next topic");
  
  // Check for simpler "move on" messages from components like ExperienceHistogram
  const isMoveOnRequest = message && 
    (message.toLowerCase().includes("let's move on to the next topic") || 
     message.toLowerCase().includes("lets move on to the next topic") ||
     message.toLowerCase().includes("suggested titles look good") ||
     message.toLowerCase().includes("move on to next topic"));

  // Check for session restoration confirmation messages
  const isSessionRestorationConfirmation = message && 
    (message.includes("Yes, these skills look good. Let's move on to the next topic.") ||
     message.includes("Yes, these titles look good. Let's move on to the next topic.") ||
     message.includes("Yes, these locations look good. Let's move on to the next topic.") ||
     message.includes("Yes, these company stages look good. Let's move on to the next topic.") ||
     message.includes("Yes, this experience range looks good. Let's move on to the next topic."));
  
  return isOldFormatSkip || isNewFormatSkip || isMoveOnRequest || isSessionRestorationConfirmation;
};

/**
 * Check if the message is a title rejection request
 * @param {string} message - The message to check
 * @returns {boolean} True if the message is a title rejection request
 */
const isTitleRejectionRequest = (message) => {
  return message && 
    message.toLowerCase().includes("i'll specify my own titles instead of using the suggested ones");
};

/**
 * Extract the topic name from a skip request message
 * @param {string} message - The skip request message
 * @returns {string|null} The topic name or null if not found
 */
const extractTopicFromSkipRequest = (message) => {
  if (!isSkipRequest(message)) return null;  
  
  // Try to extract from old standard skip format
  const oldFormatRegex = /I'd like to skip the (\w+) discovery/;
  const oldFormatMatch = message.match(oldFormatRegex);
  if (oldFormatMatch) return oldFormatMatch[1];
  
  // Try to extract from new standard skip format
  const newFormatRegex = /I am done with my selections for (\w+) and am ready/i;
  const newFormatMatch = message.match(newFormatRegex);
  if (newFormatMatch) {
    // Convert the first letter to lowercase since topic names are stored in lowercase
    const topicName = newFormatMatch[1].toLowerCase();
    return topicName;
  }
  
  // For simpler move-on messages, we can't extract the topic name directly
  // In this case, we'll return a special value that indicates we should use the current topic
  return "CURRENT_TOPIC";
};

// URL parsing and processing is now handled in useMessaging.jsx

export const processUserMessage = async (
  userMessage,
  messages,
  currentDiscoveryState,
  advanceToNextTopic,
  queries,
  setMessages,
  onSendText,
  updateRequirements,
  getAllRequirements,
  isJdUrl = false,
  setTitleKeys = null,
  setConfirmedSkills = null,
  discoveryProgress = null,
  sessionId = null,
  directTopicAdvancement = null // New parameter for direct topic advancement callback
) => {
  // Default decision is to send to backend with original message
  let decision = {
    actionType: 'SEND_TO_BACKEND',
    message: userMessage,
    agentGuidance: null,
    updatedDiscoveryState: currentDiscoveryState,
    queries,
    currentTopic: currentDiscoveryState?.currentDiscoTopic || null
  };
  // Log the current discovery state
  console.log('Current discovery state:', {
    currentTopic: currentDiscoveryState?.currentDiscoTopic,
    discoveryOrder: currentDiscoveryState?.discoveryOrder,
    completedTopics: currentDiscoveryState?.completedTopics,
    roleType: currentDiscoveryState?.roleType
  });
  
  // Debug logging for companies topic
  if (currentDiscoveryState?.currentDiscoTopic === 'companies') {
    console.log('🏢 PROCESSUSERMESSAGE - Companies topic flow debug:', {
      userMessage,
      isTitleRejection: isTitleRejectionRequest(userMessage),
      isSkip: isSkipRequest(userMessage),
      currentTopic: currentDiscoveryState?.currentDiscoTopic
    });
  }

  // Check if this is a title rejection request
  if (isTitleRejectionRequest(userMessage)) {
    console.log('Title rejection request detected:', userMessage);
    
    // Check if the current topic is 'title'
    if (currentDiscoveryState?.currentDiscoTopic === 'title') {
      console.log('Setting actionType to USER_REJECTED_TITLE_SUGGESTIONS');
      
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg => 
            msg.role === 'AI' && msg.content === 'Typing...' 
              ? { 
                  ...msg, 
                  thinking: `User rejected title suggestions. Setting actionType to USER_REJECTED_TITLE_SUGGESTIONS.` 
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      // Return a decision with USER_REJECTED_TITLE_SUGGESTIONS action type
      return {
        ...decision,
        actionType: 'USER_REJECTED_TITLE_SUGGESTIONS',
        message: userMessage,
        updatedDiscoveryState: currentDiscoveryState,
        queries
      };
    }
  }
  
  // Check if this is a skip request from the SkipTopicButton
  if (isSkipRequest(userMessage)) {
    console.log('Skip request detected:', userMessage);
    if (currentDiscoveryState?.currentDiscoTopic === 'companies') {
      console.log('🏢 WARNING: Skip request detected for companies topic - this may block companyQueueStrategy');
    }
    const topicToSkip = extractTopicFromSkipRequest(userMessage);
    console.log('Topic to skip:', topicToSkip);
    
    // Verify that the topic to skip matches the current topic or is the special "CURRENT_TOPIC" value
    if (topicToSkip && (topicToSkip === currentDiscoveryState?.currentDiscoTopic || topicToSkip === "CURRENT_TOPIC")) {
      console.log(`Skipping topic: ${topicToSkip}`);
      
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg => 
            msg.role === 'AI' && msg.content === 'Typing...' 
              ? { 
                  ...msg, 
                  thinking: `User requested to skip topic "${topicToSkip}". Advancing to the next topic...` 
                }
              : msg
          );
          return updatedMessages;
        });
      }

      // Advance to the next topic
      let updatedDiscoveryState;
      try {
        updatedDiscoveryState = advanceToNextTopic();
        console.log('advanceToNextTopic() returned:', updatedDiscoveryState);
      } catch (error) {
        console.error('Error calling advanceToNextTopic():', error);
        return decision; // Return the original decision if there's an error
      }
      
      // If we've completed all topics
      if (!updatedDiscoveryState.currentDiscoTopic) {
        // Update thinking accordion
        if (setMessages) {
          setMessages(prev => {
            const updatedMessages = prev.map(msg => 
              msg.role === 'AI' && msg.content === 'Typing...' 
                ? { 
                    ...msg, 
                    thinking: `${msg.thinking}\nAll topics completed. Generating summary...` 
                  }
                : msg
            );
            return updatedMessages;
          });
        }
        
        // Return decision for completed discovery
        return {
          actionType: 'DISCOVERY_COMPLETE',
          message: userMessage,
          agentGuidance: "All topics have been covered. Provide a summary of the information gathered and ask if there's anything else the user would like to add.",
          updatedDiscoveryState,
          queries
        };
      }
      
      // Get the next topic and role type
      const nextTopic = updatedDiscoveryState.currentDiscoTopic;
      const roleType = updatedDiscoveryState.roleType;
      
      console.log(`Moving to next topic: ${nextTopic}, role type: ${roleType}`);
      
      // Get the strategy for the next topic
      const nextTopicStrategy = getStrategyForRoleType(nextTopic, roleType);
      
      // Create messages with current text for API calls
      const messagesWithCurrentText = [
        ...messages,
        { role: 'User', content: userMessage }
      ];
      
      // Create decision for the next topic with INIT phase
      const nextTopicDecision = {
        actionType: 'ADVANCE_TOPIC',
        message: userMessage,
        agentGuidance: `Moving to the next topic: ${nextTopic}`,
        updatedDiscoveryState,
        queries,
        currentTopic: nextTopic,
        strategyPhase: StrategyPhase.INIT, // Explicitly set to INIT phase
        advanceToNextTopic,
        onSendText,
        updateRequirements
      };
      
      // Directly call the next topic strategy with INIT phase
      return await nextTopicStrategy(nextTopicDecision, messagesWithCurrentText, setMessages);
    }
  }

  // URL detection and processing is now handled in useMessaging.jsx

  try {
    if (isJdUrl) {
      console.log('Processing JD URL');
      
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg => 
            msg.role === 'AI' && msg.content === 'Typing...' 
              ? { 
                  ...msg, 
                  thinking: `${msg.thinking || ''}\nProcessing JD URL` 
                }
              : msg
          );
          return updatedMessages;
        });
      }
    }
    
    // CASE 1: Advance to next topic (if requested)
    if (false && advanceToNextTopic) {
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg => 
            msg.role === 'AI' && msg.content === 'Typing...' 
              ? { 
                  ...msg, 
                  thinking: `${msg.thinking}\nTopic complete. Advancing to the next topic...` 
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      console.log('Topic complete, calling advanceToNextTopic()...');
      
      // Advance to the next topic
      let updatedDiscoveryState;
      try {
        updatedDiscoveryState = advanceToNextTopic();
        console.log('advanceToNextTopic() returned:', updatedDiscoveryState);
      } catch (error) {
        console.error('Error calling advanceToNextTopic():', error);
        return decision; // Return the original decision if there's an error
      }
      
      // If we've completed all topics
      if (!updatedDiscoveryState.currentDiscoTopic) {
        // Update thinking accordion
        if (setMessages) {
          setMessages(prev => {
            const updatedMessages = prev.map(msg => 
              msg.role === 'AI' && msg.content === 'Typing...' 
                ? { 
                    ...msg, 
                    thinking: `${msg.thinking}\nAll topics completed. Generating summary...` 
                  }
                : msg
            );
            return updatedMessages;
          });
        }
        
        // Return decision for completed discovery
        return {
          actionType: 'DISCOVERY_COMPLETE',
          message: userMessage,
          agentGuidance: "All topics have been covered. Provide a summary of the information gathered and ask if there's anything else the user would like to add.",
          updatedDiscoveryState,
          queries
        };
      }
      
      // Get the next topic and role type
      const nextTopic = updatedDiscoveryState.currentDiscoTopic;
      const roleType = updatedDiscoveryState.roleType;
      
      console.log(`Moving to next topic: ${nextTopic}, role type: ${roleType}`);
      
      // Get the strategy for the next topic
      const nextTopicStrategy = getStrategyForRoleType(nextTopic, roleType);
      
      // Create messages with current text for API calls
      const messagesWithCurrentText = [
        ...messages,
        { role: 'User', content: userMessage }
      ];
      
      // Fetch pending queue items for the next topic
      let pendingQueueItems = [];
      if (sessionId && centralDiscoveryQueue.sessionId === sessionId) {
        try {
          pendingQueueItems = centralDiscoveryQueue.getItemsByTopic(nextTopic).filter(item => !item.disco_done);
          console.log(`🎯 TOPIC DECISION ENGINE: Found ${pendingQueueItems.length} pending items for topic "${nextTopic}"`);
        } catch (error) {
          console.error('Error fetching pending queue items:', error);
        }
      }

      // Create decision for the next topic with INIT phase
      const nextTopicDecision = {
        actionType: 'ADVANCE_TOPIC',
        message: userMessage,
        agentGuidance: `Moving to the next topic: ${nextTopic}`,
        updatedDiscoveryState,
        queries,
        currentTopic: nextTopic,
        strategyPhase: StrategyPhase.INIT, // Explicitly set to INIT phase
        advanceToNextTopic,
        onSendText,
        pendingQueueItems // Pass pending queue items to the strategy
      };
      
      // Directly call the next topic strategy with INIT phase
      return await nextTopicStrategy(nextTopicDecision, messagesWithCurrentText, setMessages);
    }
    
    // CASE 2: Topic is not complete, continue with current topic
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg => 
          msg.role === 'AI' && msg.content === 'Typing...' 
            ? { 
                ...msg, 
                thinking: `${msg.thinking}\nContinuing with current topic: ${currentDiscoveryState?.currentDiscoTopic || 'unknown'}` 
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Get the current topic and role type
    const currentTopic = currentDiscoveryState?.currentDiscoTopic;
    const roleType = currentDiscoveryState?.roleType;
    
    console.log(`Continuing with current topic: ${currentTopic}, role type: ${roleType}`);
    
    // Get the strategy for the current topic
    console.log(`🎯 CALLING getStrategyForRoleType for currentTopic: ${currentTopic}, roleType: ${roleType}`);
    const currentTopicStrategy = getStrategyForRoleType(currentTopic, roleType);
    console.log(`🎯 getStrategyForRoleType returned strategy:`, !!currentTopicStrategy);
    
    // Create messages with current text for API calls
    const messagesWithCurrentText = [
      ...messages,
      { role: 'User', content: userMessage }
    ];
    
      // Create decision for the current topic
      const currentTopicDecision = {
        ...decision,
        currentTopic: currentTopic,
        actionType: 'CONTINUE_TOPIC',
        updatedDiscoveryState: currentDiscoveryState,
        advanceToNextTopic,
        onSendText,
        updateRequirements,
        getAllRequirements,
        // Pass the isJdUrl flag to the strategy
        isJdUrl,
        // Pass setTitleKeys to the strategy if the current topic is 'title'
        setTitleKeys,
        // Pass setConfirmedSkills to the strategy for skills confirmation
        setConfirmedSkills,
        // Preserve the strategyPhase from the previous decision if it exists
        strategyPhase: currentDiscoveryState?.strategyPhase || decision.strategyPhase,
        // Pass discovery progress to the strategy
        discoveryProgress,
        // Pass session ID to the strategy
        sessionId: sessionId
      };
    
    // Apply the strategy for the current topic
    return await currentTopicStrategy(currentTopicDecision, messagesWithCurrentText, setMessages);
    
  } catch (error) {
    console.error('Error in topic decision engine:', error);
    return decision; // Return default decision on error
  }
};

export { getCustomComponent } from './topicDecisionComponents';

export default {
  processUserMessage
};
